import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Alert,
  Skeleton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useAuth from "../hooks/useAuth";
import profileApi from "../api/profileApi";
import favoritesApi from "../api/favoritesApi";
import companyApi from "../api/companyApi";
import AuthService from "../api/axiosInstance";
import endpoints from "../api/endpoints";

const SECTIONS = {
  PROFILE: "profile",
  FAVORITES: "favorites",
  CLAIMS: "claims",
  SUBSCRIPTION: "subscription",
};

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [profile, setProfile] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(SECTIONS.PROFILE);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    new_password_confirm: "",
  });
  const [passwordStatus, setPasswordStatus] = useState({ loading: false, error: null, success: null });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteStatus, setDeleteStatus] = useState({ loading: false, error: null });

  const isLoggedIn = useMemo(() => Boolean(user && (user.id || user.email || user.username)), [user]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const load = async () => {
      try {
        const [profileRes, favRes, claimRes] = await Promise.all([
          profileApi.getProfile(),
          favoritesApi.getFavorites(),
          companyApi.getClaims(),
        ]);
        setProfile(profileRes);
        setFavorites(favRes);
        setClaims(claimRes);
      } catch (err) {
        console.error("Failed to load profile data", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    return (
      <Container sx={{ mt: 6 }}>
        <Alert severity="info">Please log in to view your profile.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={{ mt: 6 }}>
        <Skeleton width="40%" height={48} />
        <Skeleton width="60%" height={32} />
        <Skeleton width="100%" height={120} />
      </Container>
    );
  }

  const cardStyle = {
    ...(theme.mixins?.frostedBox || { border: "1px solid", borderColor: "divider", borderRadius: 2 }),
    display: "flex",
    flexDirection: "column",
    gap: 0.75,
    p: 2,
    height: "100%",
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Your Profile
      </Typography>

    <Grid container spacing={3} alignItems="stretch">
        <Grid size={{xs:12, md:3}} >
          <Paper variant="outlined" sx={{ p: 1, height: "100%" }}>
            <List dense>
              <ListItemButton
                selected={activeSection === SECTIONS.PROFILE}
                onClick={() => setActiveSection(SECTIONS.PROFILE)}
              >
                <ListItemText primary="Profile" />
              </ListItemButton>
              <ListItemButton
                selected={activeSection === SECTIONS.FAVORITES}
                onClick={() => setActiveSection(SECTIONS.FAVORITES)}
              >
                <ListItemText primary="Favorited Companies" />
              </ListItemButton>
              <ListItemButton
                selected={activeSection === SECTIONS.CLAIMS}
                onClick={() => setActiveSection(SECTIONS.CLAIMS)}
              >
                <ListItemText primary="Claimed Companies" />
              </ListItemButton>
            </List>
          </Paper>
        </Grid>

        <Grid size={{xs:12, md:9}} >
          <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
            {activeSection === SECTIONS.PROFILE && (
              <ProfileSection
                user={user}
                profile={profile}
                onOpenChangePassword={() => setPasswordDialogOpen(true)}
                onOpenDeleteAccount={() => setDeleteDialogOpen(true)}
              />
            )}
            {activeSection === SECTIONS.FAVORITES && (
              <FavoritesSection favorites={favorites} cardStyle={cardStyle} />
            )}
            {activeSection === SECTIONS.CLAIMS && (
              <ClaimsSection claims={claims} cardStyle={cardStyle} navigate={navigate} />
            )}
            {activeSection === SECTIONS.SUBSCRIPTION && <SubscriptionSection />}
          </Paper>
        </Grid>
      </Grid>

      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => {
          setPasswordDialogOpen(false);
          setPasswordStatus({ loading: false, error: null, success: null });
          setPasswordForm({
            current_password: "",
            new_password: "",
            new_password_confirm: "",
          });
        }}
        form={passwordForm}
        setForm={setPasswordForm}
        status={passwordStatus}
        setStatus={setPasswordStatus}
      />

      <DeleteAccountDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteStatus({ loading: false, error: null });
          setDeleteConfirm("");
        }}
        confirmText={deleteConfirm}
        setConfirmText={setDeleteConfirm}
        status={deleteStatus}
        setStatus={setDeleteStatus}
        onDeleted={logout}
      />
    </Container>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, columnGap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function ProfileSection({ user, onOpenChangePassword, onOpenDeleteAccount }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Account
      </Typography>
      <InfoRow label="Email" value={user?.email} />
      {user?.plan && <InfoRow label="Plan" value={user.plan} />}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={onOpenChangePassword}
          >
            Change password
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onOpenDeleteAccount}
          >
            Delete account
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function FavoritesSection({ favorites, cardStyle }) {
  if (!favorites?.length) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Favorite Companies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You haven’t favorited any companies yet.
        </Typography>
        <Button component={RouterLink} to="/companies" variant="contained" size="small">
          Browse companies
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Favorites
      </Typography>
      <Grid container spacing={2}>
        {favorites.map((fav) => (
          <Grid item xs={12} sm={6} key={fav.id}>
            <Box sx={cardStyle}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {fav.company.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[
                  fav.company?.profile?.hq_city,
                  fav.company?.profile?.hq_country,
                ]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </Typography>
              <Box sx={{ mt: "auto" }}>
                <Button
                  component={RouterLink}
                  to={`/companies/${fav.company.slug}`}
                  size="small"
                  variant="contained"
                >
                  View company
                </Button>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function ClaimsSection({ claims, cardStyle, navigate }) {
  if (!claims?.length) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Company Claims
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You haven't submitted any company claims yet. If you are in investor relations or management for a mining company, you can submit a company claim.
        </Typography>
        <Button component={RouterLink} to="/companies" variant="contained" size="small">
          Find your company
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Company Claims
      </Typography>
      <Grid container spacing={2}>
        {claims.map((claim) => {
          const companyPath = claim.company_slug
            ? `/companies/${claim.company_slug}`
            : `/companies/${claim.company}`;
          return (
            <Grid item xs={12} sm={6} key={claim.id}>
              <Box sx={cardStyle}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {claim.company_name}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: "auto" }}>
                  <Button variant="contained" size="small" onClick={() => navigate(companyPath)}>
                    View company
                  </Button>
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function SubscriptionSection() {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Coming soon. In the future you’ll be able to manage billing and featured listings here.
      </Typography>
      <Button variant="outlined" size="small" component="a" href="mailto:sales@example.com">
        Contact sales
      </Button>
    </Box>
  );
}

function ChangePasswordDialog({ open, onClose, form, setForm, status, setStatus }) {
  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: null, success: null });
    try {
      await AuthService.post(endpoints.PASSWORD_CHANGE, form);
      setStatus({ loading: false, error: null, success: "Password updated." });
      setForm({
        current_password: "",
        new_password: "",
        new_password_confirm: "",
      });
    } catch (err) {
      const data = err?.response?.data || {};
      const detail =
        data.detail ||
        data.password ||
        data.non_field_errors ||
        "Unable to change password.";
      setStatus({ loading: false, error: detail, success: null });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {status.error && <Alert severity="error">{status.error}</Alert>}
          {status.success && <Alert severity="success">{status.success}</Alert>}
          {!status.success && (
            <>
              <TextField
                label="Current password"
                type="password"
                fullWidth
                value={form.current_password}
                onChange={handleChange("current_password")}
                autoComplete="current-password"
              />
              <TextField
                label="New password"
                type="password"
                fullWidth
                value={form.new_password}
                onChange={handleChange("new_password")}
                autoComplete="new-password"
              />
              <TextField
                label="Confirm new password"
                type="password"
                fullWidth
                value={form.new_password_confirm}
                onChange={handleChange("new_password_confirm")}
                autoComplete="new-password"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {!status.success && (
            <Button variant="contained" type="submit" disabled={status.loading}>
              {status.loading ? "Saving..." : "Update"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

function DeleteAccountDialog({ open, onClose, confirmText, setConfirmText, status, setStatus, onDeleted }) {
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (confirmText.trim().toLowerCase() !== "delete") {
      setStatus({ loading: false, error: "Type DELETE to confirm account deletion." });
      return;
    }

    setStatus({ loading: true, error: null });
    try {
      await profileApi.deleteAccount();
      await onDeleted();
    } catch (err) {
      const data = err?.response?.data || {};
      const detail = data.detail || "Unable to delete account.";
      setStatus({ loading: false, error: detail });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <Alert severity="warning">
            This will permanently delete your account and associated data.
          </Alert>
          {status.error && <Alert severity="error">{status.error}</Alert>}
          <TextField
            label='Type "DELETE" to confirm'
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="error" type="submit" disabled={status.loading}>
            {status.loading ? "Deleting..." : "Delete account"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
